<template>
  <div>
    <!-- Receive Return Modal -->
    <BaseModal
      :value="showReceiveModal"
      @input="showReceiveModal = $event"
      :allowClose="true"
      :clickOutsideClose="false"
      :showHeader="false"
    >
      <template v-slot:header>
        <h2>{{ $t("message.returns.modal_receive_title") }}</h2>
      </template>

      <div class="p-4">
        <h2 class="text-xl font-bold mb-4">
          {{ $t("message.returns.modal_receive_title") }}
        </h2>
        <p class="text-sm text-gray-600 mb-4">
          {{ $t("message.returns.modal_receive_description") }}
        </p>
        <div v-if="loadingModalData" class="flex justify-center py-4">
          <div class="animate-spin">⏳</div>
        </div>
        <div v-else class="space-y-4 max-h-96 overflow-y-auto mb-4">
          <div
            v-for="product in currentReturnProducts"
            :key="product.sku"
            class="border border-gray-200 rounded p-3"
          >
            <div class="flex items-start gap-2 mb-2">
              <input
                v-model="product.selected"
                @change="onProductSelected(product)"
                type="checkbox"
                :id="'product-' + product.sku"
                class="mt-1"
              />
              <div class="flex-1">
                <label
                  :for="'product-' + product.sku"
                  class="font-semibold text-sm cursor-pointer"
                >
                  {{ product.description }}
                </label>
                <p class="text-xs text-gray-500">SKU: {{ product.sku }}</p>
              </div>
            </div>
            <label v-if="product.selected" class="text-xs text-gray-700">
              {{ $t("message.returns.modal_received_qty") }} ({{
                $t("message.returns.modal_of")
              }}
              {{ product.qty }}):
            </label>
            <div v-if="product.selected" class="flex gap-2 items-center">
              <button
                @click="decrementQuantity(product)"
                type="button"
                class="bg-gray-300 hover:bg-gray-400 text-black px-3 py-1 rounded text-sm font-bold"
              >
                −
              </button>
              <input
                v-model.number="product.received_qty"
                type="number"
                min="0"
                :max="product.qty"
                class="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
              />
              <button
                @click="incrementQuantity(product)"
                type="button"
                class="bg-gray-300 hover:bg-gray-400 text-black px-3 py-1 rounded text-sm font-bold"
              >
                +
              </button>
            </div>
            <div v-if="product.selected" class="mt-2">
              <label class="text-xs text-gray-700 block mb-1">
                {{ $t("message.returns.modal_condition") }} ({{
                  $t("message.returns.modal_optional")
                }}):
              </label>
              <input
                v-model="product.condition"
                type="text"
                maxlength="100"
                placeholder="e.g., Damaged, Used, Like New..."
                class="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <button
            @click="submitReceive"
            class="btn-success-button flex-1"
            :disabled="receivingReturn || loadingModalData"
          >
            {{
              receivingReturn
                ? $t("message.returns.modal_saving")
                : $t("message.returns.modal_confirm_receipt")
            }}
          </button>
          <button
            @click="closeReceiveModal"
            class="btn-clear-button flex-1"
            :disabled="receivingReturn"
          >
            {{ $t("message.returns.modal_cancel") }}
          </button>
        </div>
      </div>
    </BaseModal>

    <!-- Process Refund Modal -->
    <BaseModal
      :value="showRefundModal"
      @input="showRefundModal = $event"
      :allowClose="true"
      :clickOutsideClose="false"
      :showHeader="false"
    >
      <template v-slot:header>
        <h2>{{ $t("message.returns.modal_refund_title") }}</h2>
      </template>

      <div class="p-4">
        <h2 class="text-xl font-bold mb-4">
          {{ $t("message.returns.modal_refund_title") }}
        </h2>
        <div class="space-y-4 mb-4">
          <div>
            <label class="text-sm font-semibold">{{
              $t("message.returns.modal_refund_amount")
            }}</label>
            <input
              v-model.number="refundData.refund_amount"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 border border-gray-300 rounded"
            />
          </div>
          <div>
            <label class="text-sm font-semibold">{{
              $t("message.returns.modal_refund_method")
            }}</label>
            <select
              v-model="refundData.refund_method"
              class="w-full px-3 py-2 border border-gray-300 rounded"
            >
              <option value="">{{
                $t("message.returns.modal_select_method")
              }}</option>
              <option value="original_payment">{{
                $t("message.returns.refund_method_original")
              }}</option>
              <option value="store_credit">{{
                $t("message.returns.refund_method_store_credit")
              }}</option>
              <option value="check">{{
                $t("message.returns.refund_method_check")
              }}</option>
            </select>
          </div>
        </div>
        <div class="flex gap-2">
          <button
            @click="submitRefund"
            class="btn-success-button flex-1"
            :disabled="
              processingRefund ||
                !refundData.refund_amount ||
                !refundData.refund_method
            "
          >
            {{
              processingRefund
                ? $t("message.returns.modal_processing")
                : $t("message.returns.modal_process_refund")
            }}
          </button>
          <button
            @click="closeRefundModal"
            class="btn-clear-button flex-1"
            :disabled="processingRefund"
          >
            {{ $t("message.returns.modal_cancel") }}
          </button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import {
  receiveReturn,
  refundReturn,
  getReturn
} from "@/api/store/ReturnsService";
import BaseModal from "@/components/global/_base-modal.vue";

export default {
  components: {
    BaseModal
  },
  props: {
    returnId: {
      type: String,
      required: false,
      default: null
    },
    returnData: {
      type: Object,
      required: false,
      default: null
    },
    showReceiveModal: {
      type: Boolean,
      required: false,
      default: false
    },
    showRefundModal: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      receivingReturn: false,
      processingRefund: false,
      loadingModalData: false,
      currentReturnProducts: [],
      refundData: {
        refund_amount: 0,
        refund_method: ""
      }
    };
  },
  computed: {
    selectedProducts() {
      return this.currentReturnProducts.filter(p => p.selected);
    }
  },
  watch: {
    showReceiveModal(newVal) {
      if (newVal && this.returnId) {
        this.loadReceiveModalData();
      }
    },
    showRefundModal(newVal) {
      if (newVal && this.returnData) {
        this.refundData = {
          refund_amount:
            this.returnData.total_refundable_amount ||
            this.returnData.totalRefundableAmount ||
            this.returnData.estimated_refund_amount ||
            this.returnData.estimatedRefundAmount ||
            0,
          refund_method: ""
        };
      }
    }
  },
  methods: {
    onProductSelected(product) {
      if (product.selected) {
        product.received_qty = product.qty;
      } else {
        product.received_qty = 0;
      }
    },
    incrementQuantity(product) {
      if (product.received_qty < product.qty) {
        product.received_qty++;
      }
    },
    decrementQuantity(product) {
      if (product.received_qty > 0) {
        product.received_qty--;
      }
    },
    async loadReceiveModalData() {
      this.loadingModalData = true;
      try {
        const fullReturn = await getReturn(this.returnId);
        if (fullReturn && fullReturn.products) {
          this.currentReturnProducts = fullReturn.products.map(p => ({
            ...p,
            selected: false,
            received_qty: 0,
            condition: ""
          }));
        }
      } catch (error) {
        console.error("Error loading return data:", error);
        this.$toast.open({
          message: "Error loading return products",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loadingModalData = false;
      }
    },
    closeReceiveModal() {
      this.$emit("update:showReceiveModal", false);
    },
    closeRefundModal() {
      this.$emit("update:showRefundModal", false);
    },
    async submitReceive() {
      if (!this.returnId || this.selectedProducts.length === 0) return;

      this.receivingReturn = true;
      try {
        const products = this.selectedProducts.map(p => ({
          sku: p.sku,
          received_qty: p.received_qty,
          condition: p.condition || ""
        }));

        await receiveReturn(this.returnId, products);

        this.$toast.open({
          message: this.$t("message.returns.success_receive"),
          type: "success",
          position: "top-right"
        });

        this.closeReceiveModal();
        this.$emit("returnUpdated");
      } catch (error) {
        this.$toast.open({
          message: error.message || this.$t("message.returns.error_receive"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.receivingReturn = false;
      }
    },
    async submitRefund() {
      if (
        !this.returnId ||
        !this.refundData.refund_amount ||
        !this.refundData.refund_method
      ) {
        return;
      }

      this.processingRefund = true;
      try {
        await refundReturn(this.returnId, this.refundData);

        this.$toast.open({
          message: this.$t("message.returns.success_refund"),
          type: "success",
          position: "top-right"
        });

        this.closeRefundModal();
        this.$emit("returnUpdated");
      } catch (error) {
        let errorMessage = this.$t("message.returns.error_refund");

        // Handle API error response format: {status: false, error: {message: "..."}}
        if (error.response && error.response.data) {
          const responseData = error.response.data;
          if (
            responseData.status === false &&
            responseData.error &&
            responseData.error.message
          ) {
            errorMessage = responseData.error.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$toast.open({
          message: errorMessage,
          type: "error",
          position: "top-right",
          timeout: 8000
        });
      } finally {
        this.processingRefund = false;
      }
    }
  }
};
</script>
